import { blue, green, red } from '@mui/material/colors'
import {
  ActionMessage,
  Device,
  DeviceMessage,
  GeoCoordinate,
  Spine,
  SpineActionMessage,
  SpineConfigMessage,
  SpineMessage,
  SpineStatusMessage,
  Valve
} from '../types'
import { DeviceSignal } from '../enums/DeviceSignal'
import { DeviceType } from '../enums'
import i18next from 'i18next'
import {
  formatValveMessage,
  isValveActionMessage,
  isValveMessage,
  isValveStatusMessage
} from './valve'

enum Helsinki {
  LAT = 60.169857,
  LONG = 24.938379
}

export function getDeviceGeoCoordinate(device: Device): GeoCoordinate {
  // FIXME: Throw error when device's geo coordinates cannot be
  // resolved instead of using Helsinki's coordinates as fallback.

  const lat =
    device?.asset?.project?.lat !== undefined && device?.asset?.project?.lat !== 0
      ? device?.asset?.project?.lat
      : Helsinki.LAT

  const long =
    device?.asset?.project?.long !== undefined && device?.asset?.project?.long !== 0
      ? device?.asset?.project?.long
      : Helsinki.LONG

  return {
    lat,
    long
  }
}

export function checkRssiColor(value: number) {
  if (value > -80 || value == -255) {
    return green[500]
  } else if (value == -254) {
    return blue[500]
  } else return red[500]
}

export function checkSignal(value: number) {
  if (value > -60) {
    return DeviceSignal.EXCELLENT as string
  } else if (value > -70) {
    return DeviceSignal.VERY_GOOD as string
  } else if (value > -80) {
    return DeviceSignal.GOOD as string
  } else if (value > -90) {
    return DeviceSignal.LOW as string
  } else if (value < -90 && value > -254) {
    return DeviceSignal.VERY_LOW as string
  } else if (value === -254) {
    return DeviceSignal.BLUETOOTH as string
  } else if (value === -255) {
    return DeviceSignal.SUB_DEVICE as string
  }
}

export function isValve(device: Device): device is Valve {
  return device.type === DeviceType.VALVE
}

export function isSpine(device: Device): device is Spine {
  return device.type === DeviceType.SPINE
}

export function isVirtualDevice(device: Device): boolean {
  return device.isVirtual
}

export function isNotVirtualDevice(device: Device): boolean {
  return !device.isVirtual
}

export function isSpineMessage(message: DeviceMessage): message is SpineMessage {
  return (
    isSpineActionMessage(message) || isSpineConfigMessage(message) || isSpineStatusMessage(message)
  )
}

export function isSpineActionMessage(message: DeviceMessage): message is SpineActionMessage {
  return message.kind === 'SpineAction'
}

export function isSpineConfigMessage(message: DeviceMessage): message is SpineConfigMessage {
  return message.kind === 'SpineConfig'
}

export function isSpineStatusMessage(message: DeviceMessage): message is SpineStatusMessage {
  return message.kind === 'SpineStatus'
}

export function formatDeviceMessage(message: DeviceMessage): string {
  if (isSpineMessage(message)) {
    return formatSpineMessage(message)
  } else if (isValveMessage(message)) {
    return formatValveMessage(message)
  } else {
    return i18next.t('unknownDeviceMessageWasSent', { ns: 'device' })
  }
}

export function formatDeviceMessageType(message: DeviceMessage): string {
  if (isValveActionMessage(message)) {
    return i18next.t('action', { ns: 'device' })
  } else if (isValveStatusMessage(message)) {
    return i18next.t('status', { ns: 'device' })
  } else if (isSpineActionMessage(message)) {
    return i18next.t('action', { ns: 'device' })
  } else if (isSpineConfigMessage(message)) {
    return i18next.t('config', { ns: 'device' })
  } else if (isSpineStatusMessage(message)) {
    return i18next.t('status', { ns: 'device' })
  } else {
    return i18next.t('unknown', { ns: 'device' })
  }
}

export function formatSpineMessage(message: SpineMessage): string {
  if (isSpineActionMessage(message)) {
    return formatSpineActionMessage(message)
  } else if (isSpineConfigMessage(message)) {
    return formatSpineConfigMessage(message)
  } else if (isSpineStatusMessage(message)) {
    return formatSpineStatusMessage(message)
  } else {
    return i18next.t('unknownSpineMessageWasSent', { ns: 'device' })
  }
}

export function formatSpineConfigMessage(_: SpineConfigMessage) {
  return i18next.t('spineConfigWasSent', { ns: 'device' })
}

export function formatSpineStatusMessage(message: SpineStatusMessage) {
  if (isRebootMessage(message)) {
    return i18next.t('rebootStatusWasSent', { ns: 'device' })
  } else if (isADCMessage(message)) {
    return i18next.t('adcStatusWasSent', { ns: 'device' })
  } else {
    return i18next.t('spineStatusWasSent', { ns: 'device' })
  }
}

export function formatSpineActionMessage(message: SpineActionMessage) {
  if (isMultiAction(message)) {
    return isActionMessageSentByActor(message)
      ? i18next.t('actorSentSpineAction', { ns: 'device', actor: message.actorName })
      : i18next.t('spineActionWasSent', { ns: 'device' })
  } else if (isRebootAction(message)) {
    return formatRebootActionMessage(message)
  } else if (isRelayAction(message)) {
    return formatRelayActionMessage(message)
  } else if (isDfuAction(message)) {
    return formatDfuActionMessage(message)
  } else if (isSetEnergyCounterAction(message)) {
    return formatEnergyCounterActionMessage(message)
  } else if (isClearEnergyCounterAction(message)) {
    return formatClearEnergyCounterActionMessage(message)
  } else if (isGetStatusAction(message)) {
    return formatGetStatusActionMessage(message)
  } else if (isActivateRuuviTagPairingAction(message)) {
    return formatActivateRuuviTagPairingActionMessage(message)
  } else if (isGetADCDataAction(message)) {
    return formatADCActionMessage(message)
  } else if (isRmsAction(message)) {
    return formatRmsActionMessage(message)
  } else if (isGetRuuviTagHistoryAction(message)) {
    return formatGetRuuviTagHistoryActionMessage(message)
  } else if (isGetRuuviTagConditionAction(message)) {
    return formatGetRuuviTagConditionActionMessage(message)
  } else {
    return formatUnknownActionMessage(message)
  }
}

function formatRebootActionMessage(message: SpineActionMessage): string {
  return isActionMessageSentByActor(message)
    ? i18next.t('actorSentRebootAction', {
        ns: 'device',
        actor: message.actorName,
        seconds: message.reboot
      })
    : i18next.t('rebootActionWasSent', { ns: 'device', seconds: message.reboot })
}

function formatRelayActionMessage(message: SpineActionMessage): string {
  const isSentByActor = isActionMessageSentByActor(message)

  switch (message.relay) {
    case 1:
      return isSentByActor
        ? i18next.t('actorTurnedOnRelay', { ns: 'device', actor: message.actorName })
        : i18next.t('relayWasTurnedOn', { ns: 'device' })
    case 0:
    default:
      return isSentByActor
        ? i18next.t('actorTurnedOffRelay', { ns: 'device', actor: message.actorName })
        : i18next.t('relayWasTurnedOff', { ns: 'device' })
  }
}

function formatDfuActionMessage(message: SpineActionMessage) {
  const isSentByActor = isActionMessageSentByActor(message)

  switch (message.dfu) {
    case -25:
      return isSentByActor
        ? i18next.t('actorRequestedFirmwareUpdateToValve', {
            ns: 'device',
            actor: message.actorName
          })
        : i18next.t('firmwareUpdateToValveWasRequested', { ns: 'device' })
    case -26:
      return isSentByActor
        ? i18next.t('actorRequestedFirmwareUpdateToSpine', {
            ns: 'device',
            actor: message.actorName
          })
        : i18next.t('firmwareUpdateToSpineWasRequested', { ns: 'device' })
    case 99:
      return isSentByActor
        ? i18next.t('actorRequestedFirmwareUpdateViaFleetControl', {
            ns: 'device',
            actor: message.actorName
          })
        : i18next.t('firmwareUpdateViaFleetControlWasRequested', { ns: 'device' })
    default:
      return isSentByActor
        ? i18next.t('actorRequestedFirmwareUpdateInSeconds', {
            ns: 'device',
            actor: message.actorName,
            seconds: message.dfu
          })
        : i18next.t('firmwareUpdateWasRequested', {
            ns: 'device',
            seconds: message.dfu
          })
  }
}

function formatRmsActionMessage(message: SpineActionMessage) {
  const isSentByActor = isActionMessageSentByActor(message)
  switch (message.u_rms) {
    case 1:
      return isSentByActor
        ? i18next.t('actorSetTrueRMSVoltageMeasurement', {
            ns: 'device',
            actor: message.actorName
          })
        : i18next.t('trueRMSVoltageMeasurmentWasSet', { ns: 'device' })

    case 0:
    default:
      return isSentByActor
        ? i18next.t('actorSetDefaultVoltageMeasurement', {
            ns: 'device',
            actor: message.actorName
          })
        : i18next.t('defaultVoltageMeasurementWasSet', { ns: 'device' })
  }
}

function formatEnergyCounterActionMessage(message: SpineActionMessage) {
  return isActionMessageSentByActor(message)
    ? i18next.t('actorSetNewEnergyCounter', {
        ns: 'device',
        actor: message.actorName,
        energyCounter: message.energy_counter
      })
    : i18next.t('newEnergyCounterWasSet', {
        ns: 'device',
        energyCounter: message.energy_counter
      })
}

function formatClearEnergyCounterActionMessage(message: SpineActionMessage) {
  return isActionMessageSentByActor(message)
    ? i18next.t('actorClearedEnergyCounter', { ns: 'device', actor: message.actorName })
    : i18next.t('energyCounterWasCleared', { ns: 'device' })
}

function formatGetStatusActionMessage(message: SpineActionMessage) {
  return isActionMessageSentByActor(message)
    ? i18next.t('actorRequestedSpineStatus', { ns: 'device', actor: message.actorName })
    : i18next.t('spineStatusWasRequested', { ns: 'device' })
}

function formatActivateRuuviTagPairingActionMessage(message: SpineActionMessage) {
  return isActionMessageSentByActor(message)
    ? i18next.t('actorActivatedRuuviTagPairing', { ns: 'device', actor: message.actorName })
    : i18next.t('ruuviTagPairingWasActivated', { ns: 'device' })
}

function formatADCActionMessage(message: SpineActionMessage) {
  return isActionMessageSentByActor(message)
    ? i18next.t('actorRequestedADCData', { ns: 'device', actor: message.actorName })
    : i18next.t('adcActionWasSent', { ns: 'device' })
}

function formatGetRuuviTagHistoryActionMessage(message: SpineActionMessage) {
  const ruuvitag =
    Array.isArray(message?.ruuvi_history) && message.ruuvi_history.length > 0
      ? message.ruuvi_history[0]
      : undefined

  return isActionMessageSentByActor(message)
    ? i18next.t('actorRequestedRuuviTagHistory', {
        ns: 'device',
        actor: message.actorName,
        ruuvitag
      })
    : i18next.t('ruuviTagHistoryWasRequested', {
        ns: 'device',
        ruuvitag
      })
}

function formatGetRuuviTagConditionActionMessage(message: SpineActionMessage) {
  return isActionMessageSentByActor(message)
    ? i18next.t('actorRequestedRuuviTagCondition', {
        ns: 'device',
        actor: message.actorName,
        ruuvitag: message.ruuvi_get_cond
      })
    : i18next.t('ruuviTagConditionWasRequested', { ns: 'device', ruuvitag: message.ruuvi_get_cond })
}

function formatUnknownActionMessage(message: SpineActionMessage) {
  return isActionMessageSentByActor(message)
    ? i18next.t('actorSentUnknownSpineAction', { ns: 'device', actor: message.actorName })
    : i18next.t('unknownSpineActionWasSent', { ns: 'device' })
}

function isRebootMessage(message: SpineStatusMessage): boolean {
  return message?.type === 'reboot'
}

function isADCMessage(message: SpineStatusMessage): boolean {
  return (
    message?.type === 'adc_raw_data' || message?.type === 'adc_raw' || message?.type === 'adc_conv'
  )
}

/**
 * Check if the action message was sent by an actor.
 */
function isActionMessageSentByActor(message: ActionMessage) {
  return message?.actorName !== undefined && message?.actorName !== ''
}

function isMultiAction(message: SpineActionMessage): boolean {
  return (
    [
      isRebootAction(message),
      isRelayAction(message),
      isDfuAction(message),
      isSetEnergyCounterAction(message),
      isClearEnergyCounterAction(message),
      isGetStatusAction(message),
      isActivateRuuviTagPairingAction(message),
      isGetADCDataAction(message)
    ].filter((v) => v).length > 1
  )
}

function isRebootAction(message: SpineActionMessage): boolean {
  return 'reboot' in message
}

function isRelayAction(message: SpineActionMessage): boolean {
  return 'relay' in message
}

function isDfuAction(message: SpineActionMessage): boolean {
  return 'dfu' in message
}

function isSetEnergyCounterAction(message: SpineActionMessage): boolean {
  return 'energy_counter' in message
}

function isClearEnergyCounterAction(message: SpineActionMessage): boolean {
  return 'clear_e_counter' in message
}

function isGetStatusAction(message: SpineActionMessage): boolean {
  return 'get_status' in message
}

function isActivateRuuviTagPairingAction(message: SpineActionMessage): boolean {
  return 'ruuvi_pairing' in message
}

function isGetADCDataAction(message: SpineActionMessage): boolean {
  return 'adc_data' in message
}

function isRmsAction(message: SpineActionMessage): boolean {
  return 'u_rms' in message
}

function isGetRuuviTagHistoryAction(message: SpineActionMessage) {
  return 'ruuvi_history' in message
}

function isGetRuuviTagConditionAction(message: SpineActionMessage) {
  return 'ruuvi_get_cond' in message
}

export function isSinglePhase(device: Device): boolean {
  return (
    device?.powerSupply?.phase === 1 ||
    device?.powerSupply?.phase === 2 ||
    device?.powerSupply?.phase === 3
  )
}

export function isThreePhase(device: Device): boolean {
  return device?.powerSupply?.phase === null
}

/**
 * Checks if the device measures L1 phase.
 */
export function hasL1(device: Device): boolean {
  return device?.powerSupply?.phase === null || device?.powerSupply?.phase === 1
}

/**
 * Checks if the device measures L2 phase.
 */
export function hasL2(device: Device): boolean {
  return device?.powerSupply?.phase === null || device?.powerSupply?.phase === 2
}

/**
 * Checks if the device measures L3 phase.
 */
export function hasL3(device: Device): boolean {
  return device?.powerSupply?.phase === null || device?.powerSupply?.phase === 3
}
